// scheduling/pages/OverBudgetTab.jsx
// Over-budget reporting tab — manager view (own jobs) + admin view (all jobs, charts, settings)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { fetchOverBudget } from '../services/obData';
import { saveOBResponse, saveOBConfig } from '../services/schedApi';
import { BRAND } from '../utils/schedConstants';

const OB_REASONS = [
  'Budget was not realistic',
  'First year job',
  'Out of scope work not separated from the job',
  'Client delays or poor client information received',
  'Inexperienced staff led to many review notes or rework',
  'Higher bill rates due to staff mix',
  'Staff turnover',
  'Other',
];

function fmtPct(val) {
  if (val == null || isNaN(val)) return '\u2014';
  return `${Math.round(val * 100)}%`;
}

function fmtMoney(val) {
  if (val == null || isNaN(val)) return '\u2014';
  return '$' + Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtRate(val) {
  if (val == null || isNaN(val) || val === 0) return '\u2014';
  return '$' + Number(val).toFixed(0);
}

function fmtNum(val) {
  if (val == null || isNaN(val)) return '\u2014';
  return Number(val).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

export default function OverBudgetTab({ myEmail, username }) {
  const { isSuperUser } = useAuth();

  // Data
  const [jobs, setJobs] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI
  const [showAdmin, setShowAdmin] = useState(isSuperUser); // default ON for production committee
  const [adminSection, setAdminSection] = useState('alljobs'); // 'alljobs' | 'dashboard' | 'settings'
  const [expandedJob, setExpandedJob] = useState(null); // PEJobIndex of expanded row
  const [search, setSearch] = useState('');
  const [jmFilter, setJmFilter] = useState('all');
  const [sortCol, setSortCol] = useState('status');
  const [sortDir, setSortDir] = useState('asc');
  const [statusFilter, setStatusFilter] = useState(isSuperUser ? 'completed' : 'pending');
  const [saving, setSaving] = useState(null); // PEJobIndex being saved

  // Config editing
  const [editConfig, setEditConfig] = useState(null);

  // Load data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOverBudget()
      .then(data => {
        if (cancelled) return;
        setJobs(data.jobs || []);
        setConfig(data.config || null);
        setEditConfig(data.config ? { ...data.config } : null);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Unique JMs for filter
  const jmList = useMemo(() => {
    const set = new Set();
    for (const j of jobs) { if (j.jobManager) set.add(j.jobManager); }
    return [...set].sort();
  }, [jobs]);

  // Filtered + sorted jobs
  const filtered = useMemo(() => {
    let list = jobs;

    // Manager view: only own jobs
    if (!showAdmin) {
      const myUser = (myEmail || username || '').toLowerCase();
      list = list.filter(j => (j.jmEmail || '').toLowerCase() === myUser);
    }

    // JM filter (admin)
    if (showAdmin && jmFilter !== 'all') {
      list = list.filter(j => j.jobManager === jmFilter);
    }

    // Search
    // Status filter
    if (statusFilter !== 'all') {
      list = list.filter(j => j.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(j =>
        (j.clientCode || '').toLowerCase().includes(q) ||
        (j.clientName || '').toLowerCase().includes(q) ||
        (j.jobName || j.job_name || '').toLowerCase().includes(q) ||
        (j.jobManager || '').toLowerCase().includes(q)
      );
    }

    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      switch (sortCol) {
        case 'client': return ((a.clientCode || '') < (b.clientCode || '') ? -1 : 1) * dir;
        case 'job': return ((a.jobName || a.job_name || '') < (b.jobName || b.job_name || '') ? -1 : 1) * dir;
        case 'manager': return ((a.jobManager || '') < (b.jobManager || '') ? -1 : 1) * dir;
        case 'cyHours': return ((a.cyHours || 0) - (b.cyHours || 0)) * dir;
        case 'cyBudget': return ((a.cyBudget || 0) - (b.cyBudget || 0)) * dir;
        case 'cyBilling': return ((a.cyBilling || 0) - (b.cyBilling || 0)) * dir;
        case 'realization': return ((a.cyRealization || 0) - (b.cyRealization || 0)) * dir;
        case 'status':
        default: return (a.status === b.status ? 0 : a.status === 'pending' ? -dir : dir);
      }
    });
  }, [jobs, showAdmin, myEmail, username, jmFilter, statusFilter, search, sortCol, sortDir]);

  // Stats (for admin dashboard)
  const stats = useMemo(() => {
    const overBudget = jobs.filter(j => j.cyHours > j.cyBudget).length;
    const underRealization = jobs.filter(j => {
      const officeRate = j.office_rate || 0;
      return j.cyRealization != null && j.cyRealization < officeRate;
    }).length;
    const pending = jobs.filter(j => j.status === 'pending').length;
    return { total: jobs.length, overBudget, underRealization, pending, completed: jobs.length - pending };
  }, [jobs]);

  // Reason distribution (for admin chart)
  const reasonCounts = useMemo(() => {
    const counts = {};
    for (const j of jobs) {
      if (j.primaryReason) counts[j.primaryReason] = (counts[j.primaryReason] || 0) + 1;
    }
    return counts;
  }, [jobs]);

  // JM job counts (for admin chart)
  const jmCounts = useMemo(() => {
    const map = {};
    for (const j of jobs) {
      const name = j.jobManager || 'Unknown';
      if (!map[name]) map[name] = { pending: 0, completed: 0 };
      if (j.status === 'completed') map[name].completed++;
      else map[name].pending++;
    }
    return Object.entries(map).sort((a, b) => (b[1].pending + b[1].completed) - (a[1].pending + a[1].completed));
  }, [jobs]);

  const handleSort = useCallback((col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'cyHours' || col === 'cyBudget' || col === 'cyBilling' || col === 'realization' ? 'desc' : 'asc'); }
  }, [sortCol]);

  // Save explanation
  const handleSave = useCallback(async (job, formData) => {
    const jobIdx = job.job_idx || job.JobIndex;
    setSaving(jobIdx);
    try {
      await saveOBResponse({
        peJobIndex: jobIdx,
        clientCode: job.clientCode,
        clientName: job.clientName,
        jobName: job.jobName || job.job_name,
        respondedBy: myEmail || username,
        ...formData,
      });
      // Update local state
      setJobs(prev => prev.map(j => {
        const jIdx = j.job_idx || j.JobIndex;
        if (jIdx === jobIdx) return { ...j, status: 'completed', ...formData, respondedBy: myEmail || username };
        return j;
      }));
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSaving(null);
    }
  }, [myEmail, username]);

  // Save config + trigger OB re-sync from PE
  const [syncing, setSyncing] = useState(false);
  const handleSaveConfig = useCallback(async () => {
    if (!editConfig) return;
    try {
      // 1. Save config to rprs
      await saveOBConfig({ ...editConfig, updatedBy: myEmail || username });
      setConfig({ ...editConfig });

      // 2. Trigger OB re-sync from PE with new config
      setSyncing(true);
      await fetch('/api/peSync?target=overbudget', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });

      // 3. Reload data from rprs
      const data = await fetchOverBudget();
      setJobs(data.jobs || []);
      setConfig(data.config || null);
      setEditConfig(data.config ? { ...data.config } : null);
      setSyncing(false);
      setAdminSection('alljobs'); // switch back to All Jobs view
    } catch (err) {
      setSyncing(false);
      alert('Error saving config: ' + err.message);
    }
  }, [editConfig, myEmail, username]);

  if (loading) return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading over-budget data...</div></div>;
  if (syncing) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
      <div style={{ width: 40, height: 40, border: '4px solid #E5E7EB', borderTopColor: '#003D4C', borderRadius: '50%', animation: 'ob-spin 0.8s linear infinite' }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Refreshing over-budget data from PE...</div>
      <div style={{ fontSize: 12, color: '#9CA3AF' }}>This may take a moment</div>
      <style>{`@keyframes ob-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if (error) return <div style={{ padding: 24, color: '#DC2626' }}>Error: {error}</div>;

  const REASON_COLORS = ['#003D4C', '#356371', '#6CC24A', '#74A282', '#F59E0B', '#EF4444', '#8B5CF6', '#9CA3AF'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Filter bar */}
      <div className="sched-mgr-filters" style={{ flexWrap: 'wrap' }}>
        {!showAdmin && (
          <div style={{ fontSize: 11, color: '#9CA3AF', width: '100%' }}>
            Jobs where you are <strong style={{ color: '#6B7280' }}>Job Manager</strong> that are flagged as over budget or under realization
          </div>
        )}

        <div className="sched-mgr-filter-pills">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'completed', label: 'Completed' },
          ].map(sf => (
            <button key={sf.key}
              className={`sched-mgr-pill ${statusFilter === sf.key ? 'sched-mgr-pill--active' : ''}`}
              onClick={() => setStatusFilter(sf.key)}>
              {sf.key !== 'all' && (
                <span className="sched-mgr-pill-dot" style={{ background: sf.key === 'pending' ? '#F59E0B' : '#10B981' }} />
              )}
              {sf.label}
            </button>
          ))}
        </div>

        <input
          className="sched-mgr-search"
          type="text"
          placeholder="Search client, job, or manager..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {showAdmin && (
          <>
            <select className="sched-mgr-dept-select" value={jmFilter} onChange={e => setJmFilter(e.target.value)}>
              <option value="all">All Managers</option>
              {jmList.map(jm => <option key={jm} value={jm}>{jm}</option>)}
            </select>

            <div className="sched-mgr-filter-pills">
              {['alljobs', 'dashboard', 'settings'].map(s => (
                <button key={s} className={`sched-mgr-pill ${adminSection === s ? 'sched-mgr-pill--active' : ''}`}
                  onClick={() => setAdminSection(s)}>
                  {s === 'alljobs' ? 'All Jobs' : s === 'dashboard' ? 'Dashboard' : 'Settings'}
                </button>
              ))}
            </div>
          </>
        )}

        {isSuperUser && (
          <label className="sched-mgr-toggle">
            <input type="checkbox" checked={showAdmin} onChange={e => setShowAdmin(e.target.checked)} />
            <span className="sched-mgr-toggle-label">Admin View</span>
          </label>
        )}
      </div>

      {/* Admin Dashboard */}
      {showAdmin && adminSection === 'dashboard' && (
        <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total Flagged', value: stats.total, color: '#003D4C' },
              { label: 'Over Budget', value: stats.overBudget, color: '#EF4444' },
              { label: 'Under Realization', value: stats.underRealization, color: '#F59E0B' },
              { label: 'Pending', value: stats.pending, color: '#EA580C' },
            ].map(card => (
              <div key={card.label} style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', borderLeft: `4px solid ${card.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>{card.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: card.color, marginTop: 4 }}>{card.value}</div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Jobs by JM */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 12px' }}>Jobs by Manager</h4>
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                {jmCounts.map(([name, counts]) => {
                  const total = counts.pending + counts.completed;
                  return (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, width: 120, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>{name}</span>
                      <div style={{ flex: 1, display: 'flex', height: 16, borderRadius: 4, overflow: 'hidden', background: '#F3F4F6' }}>
                        {counts.completed > 0 && <div style={{ width: `${(counts.completed / total) * 100}%`, background: '#10B981' }} />}
                        {counts.pending > 0 && <div style={{ width: `${(counts.pending / total) * 100}%`, background: '#F59E0B' }} />}
                      </div>
                      <span style={{ fontSize: 10, color: '#9CA3AF', width: 24, textAlign: 'right' }}>{total}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Reason distribution */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 12px' }}>Primary Reason Distribution</h4>
              {Object.keys(reasonCounts).length === 0 ? (
                <div style={{ color: '#9CA3AF', fontSize: 12, padding: 20, textAlign: 'center' }}>No explanations submitted yet</div>
              ) : (
                <div>
                  {Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]).map(([reason, count], i) => (
                    <div key={reason} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: REASON_COLORS[i % REASON_COLORS.length], flexShrink: 0 }} />
                      <span style={{ fontSize: 11, flex: 1, color: '#374151' }}>{reason}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin Settings */}
      {showAdmin && adminSection === 'settings' && editConfig && (
        <div style={{ padding: 20, overflow: 'auto', flex: 1, maxWidth: 600 }}>
          <h4 style={{ fontSize: 15, fontWeight: 700, color: '#374151', margin: '0 0 16px' }}>Over Budget Configuration</h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 4 }}>Lookback Period</label>
              <select value={editConfig.LookbackMonths} onChange={e => setEditConfig(p => ({ ...p, LookbackMonths: parseInt(e.target.value) }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}>
                {[1, 2, 3, 6, 12].map(m => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 4 }}>Budget Threshold (%)</label>
              <input type="number" min={0} max={50} value={editConfig.BudgetThresholdPct}
                onChange={e => setEditConfig(p => ({ ...p, BudgetThresholdPct: parseInt(e.target.value) || 0 }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, width: 100 }} />
              <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8 }}>Flag jobs exceeding budget by this %</span>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 4 }}>Realization Cushion (%)</label>
              <input type="number" min={0} max={50} value={editConfig.RealizationThresholdPct}
                onChange={e => setEditConfig(p => ({ ...p, RealizationThresholdPct: parseInt(e.target.value) || 0 }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, width: 100 }} />
              <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8 }}>Flat % subtracted from office standard</span>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 8 }}>Office Realization Standards</label>
              {(() => {
                const standards = typeof editConfig.OfficeStandards === 'string' ? JSON.parse(editConfig.OfficeStandards || '{}') : (editConfig.OfficeStandards || {});
                return Object.entries(standards).map(([office, rate]) => (
                  <div key={office} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, width: 40 }}>{office}</span>
                    <input type="number" step={0.01} min={0} max={1} value={rate}
                      onChange={e => {
                        const updated = { ...standards, [office]: parseFloat(e.target.value) || 0 };
                        setEditConfig(p => ({ ...p, OfficeStandards: JSON.stringify(updated) }));
                      }}
                      style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, width: 80 }} />
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>{Math.round((rate || 0) * 100)}%</span>
                  </div>
                ));
              })()}
            </div>

            {config?.UpdatedBy && (
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                Last updated by {config.UpdatedBy} on {config.UpdatedAt ? new Date(config.UpdatedAt).toLocaleDateString() : 'unknown'}
              </div>
            )}

            <button
              onClick={handleSaveConfig}
              style={{ alignSelf: 'flex-start', padding: '10px 24px', borderRadius: 8, background: BRAND.brightGreen, color: BRAND.deepTeal, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}
            >
              Save Settings & Reload Data
            </button>
          </div>
        </div>
      )}

      {/* Jobs Table (manager view or admin alljobs) */}
      {(!showAdmin || adminSection === 'alljobs') && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table className="sched-mgr-table">
            <thead>
              <tr>
                <th style={{ width: 30 }} />
                <th style={{ width: 60 }}>Flags</th>
                {[
                  { key: 'client', label: 'Client' },
                  { key: 'job', label: 'Job Name' },
                  ...(showAdmin ? [{ key: 'manager', label: 'Manager' }] : []),
                  { key: 'cyHours', label: 'CY Hours', right: true },
                  { key: 'cyBudget', label: 'CY Budget', right: true },
                  { key: 'cyBilling', label: 'CY Billing', right: true },
                  { key: 'realization', label: 'CY Real.', right: true },
                  { key: 'status', label: 'Status' },
                ].map(col => {
                  const active = sortCol === col.key;
                  return (
                    <th key={col.key}
                      className={`sched-mgr-th-sortable${col.right ? ' sched-mgr-th-right' : ''}${active ? ' sched-mgr-th-sorted' : ''}`}
                      onClick={() => handleSort(col.key)}>
                      {col.label}
                      <span className={`sched-mgr-sort-arrows${active ? ' sched-mgr-sort-arrows--active' : ''}`}>
                        <span className={active && sortDir === 'asc' ? 'sched-mgr-sort-arrow--current' : ''}>{'\u25B2'}</span>
                        <span className={active && sortDir === 'desc' ? 'sched-mgr-sort-arrow--current' : ''}>{'\u25BC'}</span>
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={showAdmin ? 10 : 9} className="sched-mgr-empty">No flagged jobs match the current filters.</td></tr>
              ) : filtered.map(j => {
                const jobIdx = j.job_idx || j.JobIndex;
                const isExpanded = expandedJob === jobIdx;
                const isOverBudget = (j.cyHours || 0) > (j.cyBudget || 0);
                const isUnderRealization = j.cyRealization != null && j.office_rate != null && j.cyRealization < j.office_rate;
                const isCompleted = j.status === 'completed';
                const isOwn = (j.jmEmail || '').toLowerCase() === (myEmail || username || '').toLowerCase();
                const canEdit = isOwn; // only the JM who owns the job can edit

                return (
                  <React.Fragment key={jobIdx}>
                    <tr className={`sched-mgr-row${isCompleted ? ' ob-row-completed' : ''}`}
                      style={{ opacity: isCompleted ? 0.6 : 1 }}
                      onClick={() => setExpandedJob(isExpanded ? null : jobIdx)}>
                      <td style={{ textAlign: 'center', cursor: 'pointer', fontWeight: 700, color: '#6B7280' }}>{isExpanded ? '\u2212' : '+'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {isOverBudget && <span className="ob-flag ob-flag--budget">Over Budget</span>}
                          {isUnderRealization && <span className="ob-flag ob-flag--realization">Under Real.</span>}
                          {!j.cyBudget && <span className="ob-flag ob-flag--nobudget">No Budget</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 10, color: '#9CA3AF' }}>{j.clientCode}</div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{j.clientName}</div>
                      </td>
                      <td style={{ fontSize: 12 }}>{j.jobName || j.job_name}</td>
                      {showAdmin && <td style={{ fontSize: 12 }}>{j.jobManager}</td>}
                      <td className="sched-mgr-td-right">{fmtNum(j.cyHours)}</td>
                      <td className="sched-mgr-td-right">{fmtNum(j.cyBudget)}</td>
                      <td className="sched-mgr-td-right">{fmtMoney(j.cyBilling)}</td>
                      <td className="sched-mgr-td-right">{fmtPct(j.cyRealization)}</td>
                      <td>
                        <span className={`sched-badge ${isCompleted ? 'ob-status--completed' : 'ob-status--pending'}`}>
                          {isCompleted ? 'Completed' : 'Pending'}
                        </span>
                      </td>
                    </tr>

                    {/* Expandable detail row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={showAdmin ? 10 : 9} className="sched-expand-enter" style={{ padding: 0, background: '#F9FAFB' }}>
                          <ExpandedDetail
                            job={j}
                            canEdit={canEdit}
                            onSave={(formData) => handleSave(j, formData)}
                            saving={saving === jobIdx}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Expandable Detail / Explanation Form ──────────────────────────────────────

function ExpandedDetail({ job, canEdit, onSave, saving }) {
  const [primaryReason, setPrimaryReason] = useState(job.primaryReason || '');
  const [secondaryReasons, setSecondaryReasons] = useState(job.secondaryReasons || []);
  const [otherText, setOtherText] = useState(job.otherText || '');
  const [comments, setComments] = useState(job.comments || '');
  const [saveMsg, setSaveMsg] = useState(null);

  const hasOther = primaryReason === 'Other' || secondaryReasons.includes('Other');

  const toggleSecondary = (reason) => {
    setSecondaryReasons(prev => prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]);
  };

  const handleSubmit = async () => {
    if (!primaryReason) { setSaveMsg({ type: 'error', text: 'Primary reason is required.' }); return; }
    if (hasOther && !otherText.trim()) { setSaveMsg({ type: 'error', text: 'Please explain the "Other" reason.' }); return; }
    setSaveMsg(null);
    await onSave({ primaryReason, secondaryReasons, otherText: otherText.trim(), comments: comments.trim() });
    setSaveMsg({ type: 'success', text: 'Saved successfully.' });
  };

  // For hours/billing: increase is bad (red), decrease is good (green)
  // For realization/rate: increase is good (green), decrease is bad (red)
  const ROWS = [
    { label: 'Hours', py: job.pyHours, cy: job.cyHours, fmt: fmtNum, fmtDelta: fmtNum, badIfUp: true },
    { label: 'Budget', py: null, cy: job.cyBudget, fmt: fmtNum },
    { label: 'Billing', py: job.pyBilling, cy: job.cyBilling, fmt: fmtMoney, fmtDelta: fmtMoney, badIfUp: true },
    { label: 'Realization', py: job.pyRealization, cy: job.cyRealization, fmt: fmtPct, fmtDelta: fmtPct, badIfUp: false },
    { label: 'Eff. Rate', py: job.pyEffRate, cy: job.actualEffRate, fmt: fmtRate, fmtDelta: fmtRate, badIfUp: false },
  ];

  return (
    <div style={{ padding: '16px 24px 20px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '24px 32px', border: '1px solid #E5E7EB', maxWidth: 960, width: '100%' }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#003D4C', margin: '0 0 16px' }}>
          {job.clientCode} {'\u2014'} {job.clientName} {'\u2014'} {job.jobName || job.job_name}
        </h4>

        {/* PY | Delta | CY comparison */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0, marginBottom: 20 }}>
          {/* Prior Year (left) */}
          <div style={{ background: '#FAFAF9', borderRadius: '8px 0 0 8px', padding: '16px 20px', border: '1px solid #F3F4F6', borderRight: 'none' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Prior Year</div>
            {ROWS.map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>{row.label}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7280' }}>{row.py != null ? row.fmt(row.py) : '\u2014'}</span>
              </div>
            ))}
          </div>

          {/* Delta (center) */}
          <div style={{ background: '#F9FAFB', padding: '16px 16px', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, textAlign: 'center' }}>Change</div>
            {ROWS.map(row => {
              const hasBoth = row.py != null && row.cy != null && !isNaN(row.py) && !isNaN(row.cy) && row.fmtDelta;
              const diff = hasBoth ? row.cy - row.py : null;
              const isUp = diff != null && diff > 0;
              const isDown = diff != null && diff < 0;
              const isBad = row.badIfUp ? isUp : isDown;
              const color = diff === 0 || diff == null ? '#9CA3AF' : isBad ? '#DC2626' : '#059669';
              return (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #F3F4F6', minWidth: 100 }}>
                  {diff != null ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
                      {isUp ? '\u25B2' : isDown ? '\u25BC' : '\u2022'}{' '}
                      {diff > 0 ? '+' : ''}{row.fmtDelta(diff)}
                    </span>
                  ) : (
                    <span style={{ color: '#D1D5DB' }}>{'\u2014'}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Current Year (right) */}
          <div style={{ background: '#F9FAFB', borderRadius: '0 8px 8px 0', padding: '16px 20px', border: '1px solid #E5E7EB', borderLeft: 'none' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#003D4C', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Current Year</div>
            {ROWS.map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>{row.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{row.fmt(row.cy)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Explanation form */}
        <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Primary Reason *</label>
            <select value={primaryReason} onChange={e => setPrimaryReason(e.target.value)} disabled={!canEdit}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, background: canEdit ? '#fff' : '#F3F4F6' }}>
              <option value="">Select a primary reason...</option>
              {OB_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Secondary Reasons</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {OB_REASONS.filter(r => r !== primaryReason).map(r => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151', cursor: canEdit ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={secondaryReasons.includes(r)} onChange={() => toggleSecondary(r)} disabled={!canEdit}
                    style={{ accentColor: '#003D4C' }} />
                  {r}
                </label>
              ))}
            </div>
          </div>

          {hasOther && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Other Explanation *</label>
              <input type="text" value={otherText} onChange={e => setOtherText(e.target.value)} disabled={!canEdit}
                placeholder="Please describe..."
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Additional Comments</label>
            <textarea value={comments} onChange={e => setComments(e.target.value)} disabled={!canEdit}
              rows={3} placeholder="Any additional context..."
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>

          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={handleSubmit} disabled={saving}
                style={{ padding: '8px 20px', borderRadius: 8, background: '#003D4C', color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              {saveMsg && (
                <span style={{ fontSize: 12, fontWeight: 600, color: saveMsg.type === 'error' ? '#DC2626' : '#059669' }}>
                  {saveMsg.text}
                </span>
              )}
            </div>
          )}

          {!canEdit && (
            <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', marginTop: 4 }}>
              {job.status === 'completed'
                ? `Completed by ${job.respondedBy}`
                : 'View only \u2014 this job belongs to another manager'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
